/**
 * agentKind
 * ---------
 * Which Soteria agent a chat talks to.
 *
 * There is now exactly ONE assistant from the user's point of view: `soteria`,
 * a supervisor that routes each turn to the specialist whose role covers it
 * (see qpm-api soteria/agents/supervisor.py). Which specialist answers is an
 * implementation detail the advisor never picks and never sees.
 *
 * This replaced deriving the agent from the URL (`/clients` → client agent,
 * everything else → strategy agent), which meant asking about a strategy on
 * /clients dead-ended, the panel kept whichever agent it opened with when you
 * navigated away, and changing agent meant changing chat thread.
 *
 * `financial` / `client` stay in the union because the backend still accepts
 * them for direct specialist access (CLI, older clients, debugging without the
 * router in the way). The UI does not use them.
 */
export type AgentKind = 'soteria' | 'financial' | 'client';

/** The one assistant the UI talks to. */
export const SOTERIA_AGENT: AgentKind = 'soteria';

/**
 * Kinds whose chats can carry a focus set (attached strategies → the "Focused
 * on" chips and `strategy_ids` on the query body): the standalone strategy
 * agent, and the supervisor, which reaches the same strategy specialist.
 *
 * MUST stay in sync with `_STRATEGY_CAPABLE_KINDS` in
 * qpm-api soteria/services.py — the server only persists/resolves a focus set
 * for these kinds, so a kind the UI sends `strategy_ids` for but the server
 * doesn't accept (or vice versa) silently drops attached strategies.
 *
 * Test the set, never a single kind: gating on `kind === 'financial'` is what
 * broke focus when the UI moved to the supervisor. See agentKind.test.ts.
 */
export const STRATEGY_CAPABLE_KINDS: ReadonlySet<AgentKind> = new Set<AgentKind>(
  ['soteria', 'financial'],
);

/** Whether chats with `kind` can attach strategies (see the set above). */
export function isStrategyCapable(kind: AgentKind): boolean {
  return STRATEGY_CAPABLE_KINDS.has(kind);
}
